import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Heart, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface TabItem {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

const tabs: TabItem[] = [
  { href: '/', icon: Home, label: 'Hjem' },
  { href: '/chat', icon: Heart, label: 'Meldinger' },
  { href: '/profile', icon: User, label: 'Profil' }
];

export function TabBar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { isAuthenticated } = useAuth();
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detekter iOS enheter - kun basert på device, ikke CSS-støtte
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent) ||
                       (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    setIsIOS(isIOSDevice);
  }, []);

  // Don't show TabBar on auth page or when not authenticated (landing page)
  if (pathname === '/auth' || !isAuthenticated) {
    return null;
  }

  const safeArea = 'env(safe-area-inset-bottom, 0px)';
  const safeAreaAwareHeight = `calc(49px + ${safeArea})`;
  const indicatorTopOffset = `max(calc(${safeArea} - 21px), 0px)`;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-[var(--blur-backdrop)] flex flex-col"
      style={{
        backgroundColor: 'rgba(14, 14, 15, 1)', // Solid background to prevent bleed-through
        borderTop: '1px solid var(--color-border)',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.2)',
      }}
    >
      {/* TabBar innhold */}
      <div
        className="flex items-center justify-around px-4 flex-shrink-0"
        style={{
          height: isIOS ? '49px' : '64px',
          paddingTop: isIOS ? '10px' : '8px',
        }}
      >
        {tabs.map((tab) => {
          const isActive = pathname === tab.href || pathname?.startsWith(tab.href + '/');
          const Icon = tab.icon;
          
          return (
            <Link
              key={tab.href}
              to={tab.href}
              className="flex flex-col items-center justify-center gap-0.5 px-4 py-1 transition-all duration-[var(--motion-duration-fast)]"
              style={{
                color: isActive ? 'var(--color-bg-accent)' : 'var(--color-text-secondary)',
                transform: isActive ? 'scale(1.05)' : 'scale(1)',
              }}
            >
              <Icon
                className="w-6 h-6"
              />
              <span 
                className="font-medium"
                style={{ 
                  fontSize: 'var(--font-size-caption)',
                  fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-regular)'
                }}
              >
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
      
      {/* Home Indicator for iOS only */}
      {isIOS && (
        <div
          className="home-indicator flex-shrink-0"
          style={{
            width: '134px',
            height: '5px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '2.5px',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginTop: indicatorTopOffset,
            marginBottom: '16px',
          }}
        />
      )}

      {/* Padding spacer for non-iOS */}
      {!isIOS && (
        <div style={{ height: '10px' }} />
      )}
    </nav>
  );
}
